export interface GeoSnapshot {
  lat: number;
  lon: number;
  altitude: number | null;
  accuracy: number;
  speed: number | null;
  heading: number | null;
  timestamp: number;
}

export type GpsStatus = 'IDLE' | 'SEARCHING' | 'FIX_ACQUIRED' | 'ERROR' | 'DENIED';

export const STATIONARY_SPEED_THRESHOLD_MS = 0.3; // Speed below 0.3 m/s (~1 km/h) indicates stationary state
export const STATIONARY_DISTANCE_THRESHOLD_KM = 0.003; // Radius drift under 3 meters indicates jitter while stopped
export const STATIONARY_TIME_THRESHOLD_MS = 180000; // 3 minutes required to enter dampened stationary heartbeat
export const STATIONARY_HEARTBEAT_INTERVAL_MS = 5000; // 5-second notification heartbeat when stationary

/**
 * Calculates great-circle distance between two geographic coordinates in kilometers (Haversine formula).
 *
 * Why: Needed for stationary GPS jitter detection (< 3m drift) and breadcrumb track coordinate
 * thinning (< 5m threshold) to prevent mobile CPU strain and memory bloat.
 *
 * @param lat1 - Latitude of coordinate 1 in degrees
 * @param lon1 - Longitude of coordinate 1 in degrees
 * @param lat2 - Latitude of coordinate 2 in degrees
 * @param lon2 - Longitude of coordinate 2 in degrees
 * @returns Distance in kilometers
 */
export function calculateGeoDistanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Earth's mean radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

interface QueuedItem<T> {
  item: T;
  enqueuedAt: number;
  callback: (snapshot: GeoSnapshot | null) => void;
  timerId: ReturnType<typeof setTimeout>;
}

/**
 * High-accuracy browser geolocation tracker featuring a 5-second retroactive
 * buffering queue for packet georeferencing during GPS lock acquisition and
 * stationary dampening to conserve battery and CPU.
 */
export class LocationTracker {
  private watchId: number | null = null;
  private latestSnapshot: GeoSnapshot | null = null;
  private status: GpsStatus = 'IDLE';
  private errorMessage = '';

  private queuedItems: QueuedItem<unknown>[] = [];
  private static readonly BUFFER_TIMEOUT_MS = 5000;

  private statusHandlers: ((status: GpsStatus, accuracy?: number) => void)[] = [];
  private locationHandlers: ((pos: GeoSnapshot) => void)[] = [];

  private stationaryAnchor: { lat: number; lon: number; since: number } | null = null;
  private lastDispatchedAt = 0;

  /**
   * Activates the browser Geolocation watch stream with high accuracy settings.
   */
  start(): void {
    if (!('geolocation' in navigator)) {
      this.errorMessage = 'Geolocation API is not supported by this browser.';
      this.setStatus('ERROR');
      return;
    }

    this.setStatus('SEARCHING');

    const options: PositionOptions = {
      enableHighAccuracy: true,
      maximumAge: 5000,
      timeout: 15000
    };

    this.watchId = navigator.geolocation.watchPosition(
      (pos: GeolocationPosition) => {
        this.handlePositionUpdate(pos);
      },
      (err: GeolocationPositionError) => {
        this.handlePositionError(err);
      },
      options
    );
  }

  /**
   * Halts the Geolocation watch stream and drains remaining queued items without geo tags.
   */
  stop(): void {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
    this.setStatus('IDLE');
    this.stationaryAnchor = null;
    this.lastDispatchedAt = 0;

    // Flush any pending items waiting for a fix
    while (this.queuedItems.length > 0) {
      const q = this.queuedItems.shift()!;
      clearTimeout(q.timerId);
      q.callback(null);
    }
  }

  /**
   * Retrieves the latest GPS coordinate snapshot, or queues the packet for up to 5 seconds
   * if no GPS lock is currently available.
   *
   * @param item - Arbitrary data payload (e.g., MeshPacket)
   * @param onResolved - Callback receiving the GeoSnapshot or null if timed out
   */
  resolveLocationForPacket<T>(item: T, onResolved: (snapshot: GeoSnapshot | null) => void): void {
    // If an active GPS snapshot is available within reasonable movement thresholds:
    // Moving fast (>18 km/h): valid for 15s; Stationary/testing: valid for up to 120s
    if (this.latestSnapshot) {
      const isMovingFast = (this.latestSnapshot.speed || 0) > 5;
      const maxAgeMs = isMovingFast ? 15000 : 120000;
      if (Date.now() - this.latestSnapshot.timestamp < maxAgeMs) {
        onResolved(this.latestSnapshot);
        return;
      }
    }

    // Otherwise, enqueue the packet in the 5-second retroactive buffer waiting for an updated fix
    const timerId = setTimeout(() => {
      // Fall back to most recent available snapshot if buffer expires rather than discarding geotag
      this.flushQueuedItem(item, this.latestSnapshot || null);
    }, LocationTracker.BUFFER_TIMEOUT_MS);

    this.queuedItems.push({
      item,
      enqueuedAt: Date.now(),
      callback: onResolved,
      timerId
    });
  }

  getLatestSnapshot(): GeoSnapshot | null {
    return this.latestSnapshot;
  }

  getStatus(): GpsStatus {
    return this.status;
  }

  getErrorMessage(): string {
    return this.errorMessage;
  }

  /**
   * Returns true if the tracker has detected a stationary condition (parked/stopped >= 3 min).
   *
   * @returns Boolean indicating whether stationary dampening is currently active
   */
  isStationary(): boolean {
    if (!this.stationaryAnchor) return false;
    return Date.now() - this.stationaryAnchor.since >= STATIONARY_TIME_THRESHOLD_MS;
  }

  onStatusChange(handler: (status: GpsStatus, accuracy?: number) => void): void {
    this.statusHandlers.push(handler);
  }

  onLocationUpdate(handler: (pos: GeoSnapshot) => void): void {
    this.locationHandlers.push(handler);
  }

  private handlePositionUpdate(pos: GeolocationPosition): void {
    const coords = pos.coords;
    const now = Date.now();
    const snapshot: GeoSnapshot = {
      lat: coords.latitude,
      lon: coords.longitude,
      altitude: coords.altitude,
      accuracy: coords.accuracy,
      speed: coords.speed,
      heading: coords.heading,
      timestamp: now
    };

    this.latestSnapshot = snapshot;
    this.setStatus('FIX_ACQUIRED', snapshot.accuracy);

    // Evaluate stationary dampening
    const isMovingFast = coords.speed !== null && coords.speed !== undefined && coords.speed >= STATIONARY_SPEED_THRESHOLD_MS;

    let isStationaryState = false;
    if (isMovingFast) {
      // Actively moving: reset stationary anchor
      this.stationaryAnchor = null;
    } else {
      if (!this.stationaryAnchor) {
        this.stationaryAnchor = { lat: coords.latitude, lon: coords.longitude, since: now };
      } else {
        const distFromAnchorKm = calculateGeoDistanceKm(
          this.stationaryAnchor.lat,
          this.stationaryAnchor.lon,
          coords.latitude,
          coords.longitude
        );
        if (distFromAnchorKm >= STATIONARY_DISTANCE_THRESHOLD_KM) {
          // Moved beyond 3m radius: re-anchor
          this.stationaryAnchor = { lat: coords.latitude, lon: coords.longitude, since: now };
        } else if (now - this.stationaryAnchor.since >= STATIONARY_TIME_THRESHOLD_MS) {
          isStationaryState = true;
        }
      }
    }

    // Notify location subscribers (throttled to 5s heartbeat when stationary to conserve battery and CPU)
    const shouldDispatch = !isStationaryState || (now - this.lastDispatchedAt >= STATIONARY_HEARTBEAT_INTERVAL_MS);

    if (shouldDispatch) {
      this.lastDispatchedAt = now;
      for (const h of this.locationHandlers) {
        try {
          h(snapshot);
        } catch (err) {
          console.error('[LocationTracker] Location update handler error:', err);
        }
      }
    }

    // Flush all retroactively buffered packets with this newly arrived GPS snapshot
    while (this.queuedItems.length > 0) {
      const q = this.queuedItems.shift()!;
      clearTimeout(q.timerId);
      try {
        q.callback(snapshot);
      } catch (err) {
        console.error('[LocationTracker] Error executing queued resolution callback:', err);
      }
    }
  }

  private handlePositionError(err: GeolocationPositionError): void {
    this.errorMessage = err.message;
    if (err.code === err.PERMISSION_DENIED) {
      this.setStatus('DENIED');
    } else {
      this.setStatus('ERROR');
    }
  }

  private flushQueuedItem(item: unknown, snapshot: GeoSnapshot | null): void {
    const idx = this.queuedItems.findIndex((q) => q.item === item);
    if (idx !== -1) {
      const [q] = this.queuedItems.splice(idx, 1);
      clearTimeout(q.timerId);
      q.callback(snapshot);
    }
  }

  private setStatus(status: GpsStatus, accuracy?: number): void {
    this.status = status;
    for (const h of this.statusHandlers) {
      try {
        h(status, accuracy);
      } catch (err) {
        console.error('[LocationTracker] Status handler error:', err);
      }
    }
  }
}
